import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  listDepoimentos,
  createDepoimento,
  updateDepoimento,
  deleteDepoimento,
} from "../../services/depoimentosService";
import { uploadDepoimentoFoto, deleteDepoimentoFoto } from "../../services/uploadsService";
import ConfirmDialog from "../ui/ConfirmDialog";
import ImageUploadField from "../ui/ImageUploadField";

const EMPTY = {
  id: null,
  nome: "",
  cidade: "",
  texto: "",
  textoIt: "",
  ordem: 0,
  ativo: true,
  fotoUrl: null,
};

export default function DepoimentosSection() {
  const { C } = useTheme();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // forma em edição ou criação
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const data = await listDepoimentos();
      setLista(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleSave() {
    if (!editing.nome.trim() || !editing.texto.trim()) {
      alert("Nome e texto são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: editing.nome.trim(),
        cidade: editing.cidade.trim() || null,
        texto: editing.texto.trim(),
        textoIt: editing.textoIt.trim() || null,
        ordem: Number(editing.ordem) || 0,
        ativo: !!editing.ativo,
      };
      if (editing.id) await updateDepoimento(editing.id, payload);
      else await createDepoimento(payload);
      setEditing(null);
      await reload();
    } catch (err) {
      alert(err?.response?.data?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await deleteDepoimento(confirmDelete.id);
      setConfirmDelete(null);
      await reload();
    } catch {
      alert("Erro ao remover.");
    }
  }

  function field(key, label, multiline = false) {
    const common = {
      value: editing[key] ?? "",
      onChange: (e) => setEditing({ ...editing, [key]: e.target.value }),
      style: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${C.border}`,
        background: C.inputBg,
        color: C.text,
        fontSize: 13,
        fontFamily: "'DM Sans', sans-serif",
        boxSizing: "border-box",
        resize: multiline ? "vertical" : undefined,
      },
    };
    return (
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </label>
        {multiline ? <textarea rows={3} {...common} /> : <input {...common} />}
      </div>
    );
  }

  return (
    <div
      style={{
        background: C.bgCard,
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        padding: 24,
        marginTop: 20,
        maxWidth: 820,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: 0 }}>
            💬 Depoimentos
          </h2>
          <p style={{ color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
            Recomendado 3 a 6 depoimentos no site.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY, ordem: lista.length + 1 })}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            background: C.accent,
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          + Novo
        </button>
      </div>

      {loading ? (
        <div style={{ color: C.textMuted, fontSize: 12, padding: 12 }}>Carregando...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {lista.map((d) => (
            <div
              key={d.id}
              style={{
                padding: 12,
                background: C.bg,
                borderRadius: 10,
                borderLeft: `3px solid ${d.ativo ? C.accent : C.border}`,
                opacity: d.ativo ? 1 : 0.6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                {d.fotoUrl && (
                  <img
                    src={d.fotoUrl}
                    alt=""
                    style={{
                      width: 40, height: 40, borderRadius: "50%",
                      objectFit: "cover", flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                    #{d.ordem} · {d.nome}
                    {d.cidade && <span style={{ color: C.textMuted, fontWeight: 400 }}> — {d.cidade}</span>}
                    {!d.ativo && <span style={{ color: C.textMuted, fontSize: 10, marginLeft: 8 }}>(inativo)</span>}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                    {d.texto}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    onClick={() => setEditing(d)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: `1px solid ${C.border}`,
                      background: "transparent",
                      color: C.textMuted,
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setConfirmDelete(d)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: `1px solid ${C.red}60`,
                      background: "transparent",
                      color: C.red,
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
          {lista.length === 0 && (
            <div style={{ fontSize: 12, color: C.textMuted, fontStyle: "italic", padding: 12, textAlign: "center" }}>
              Nenhum depoimento cadastrado. Clique em "+ Novo" pra adicionar.
            </div>
          )}
        </div>
      )}

      {/* Editor inline */}
      {editing && (
        <div
          onClick={() => !saving && setEditing(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.bgCard,
              borderRadius: 14,
              border: `1px solid ${C.border}`,
              padding: 24,
              width: "100%",
              maxWidth: 520,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 14 }}>
              {editing.id ? "Editar depoimento" : "Novo depoimento"}
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
              {field("nome", "Nome *")}
              {field("cidade", "Cidade")}
            </div>
            {field("texto", "Texto (PT) *", true)}
            <div style={{ height: 10 }} />
            {field("textoIt", "Texto (IT)", true)}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              {field("ordem", "Ordem")}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Ativo
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.inputBg }}>
                  <input
                    type="checkbox"
                    checked={!!editing.ativo}
                    onChange={(e) => setEditing({ ...editing, ativo: e.target.checked })}
                    id="dep-ativo"
                  />
                  <label htmlFor="dep-ativo" style={{ fontSize: 12, color: C.text, cursor: "pointer" }}>
                    Exibir no site
                  </label>
                </div>
              </div>
            </div>

            {/* Foto opcional — só disponível após o primeiro save (precisa do id). */}
            {editing.id ? (
              <div style={{ marginTop: 14 }}>
                <ImageUploadField
                  label="Foto (opcional)"
                  hint="200×200 · exibida como avatar circular"
                  shape="circle"
                  width={80}
                  height={80}
                  value={editing.fotoUrl || null}
                  onUpload={async (file) => {
                    const url = await uploadDepoimentoFoto(editing.id, file);
                    setEditing((e) => ({ ...e, fotoUrl: url }));
                    await reload();
                  }}
                  onDelete={async () => {
                    await deleteDepoimentoFoto(editing.id);
                    setEditing((e) => ({ ...e, fotoUrl: null }));
                    await reload();
                  }}
                />
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 11, color: C.textMuted, fontStyle: "italic" }}>
                A foto fica disponível após salvar o depoimento pela primeira vez.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              <button
                type="button"
                onClick={() => !saving && setEditing(null)}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: "transparent",
                  color: C.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: saving ? "wait" : "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: saving ? "wait" : "pointer",
                  opacity: saving ? 0.7 : 1,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remover depoimento?"
        message={`Remover depoimento de ${confirmDelete?.nome ?? ""}?`}
        confirmLabel="Remover"
        danger
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </div>
  );
}
